import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type ActivityScore = {
  id: string;
  name: string;
  avatarUrl?: string;
  city?: string;
  distanceKm?: number;
  crops?: string[];
  posts7d: number;
  score: number;
};

export function useActiveNearby(
  userLat?: number,
  userLng?: number,
  radiusKm: number = 25,
  limit: number = 20
) {
  return useQuery({
    queryKey: ["active-nearby", userLat, userLng, radiusKm],
    queryFn: async () => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      // Get profiles with recent activity
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select(`
          id,
          username,
          avatar_url,
          location,
          latitude,
          longitude,
          user_id
        `)
        .eq("is_active", true)
        .eq("is_discoverable", true)
        .limit(100);

      if (error) throw error;
      if (!profiles) return [];

      // Get activity counts for the last 7 days
      const userIds = profiles.map(p => p.user_id);
      
      const [postsData, commentsData, likesData] = await Promise.all([
        supabase
          .from("posts")
          .select("user_id")
          .in("user_id", userIds)
          .gte("created_at", sevenDaysAgo.toISOString()),
        supabase
          .from("comments")
          .select("user_id")
          .in("user_id", userIds)
          .gte("created_at", sevenDaysAgo.toISOString()),
        supabase
          .from("post_likes")
          .select("user_id")
          .in("user_id", userIds)
          .gte("created_at", sevenDaysAgo.toISOString()),
      ]);

      // Count activity per user
      const activityCounts = new Map<string, { posts: number; comments: number; likes: number }>();
      
      postsData.data?.forEach(p => {
        const counts = activityCounts.get(p.user_id) || { posts: 0, comments: 0, likes: 0 };
        counts.posts++;
        activityCounts.set(p.user_id, counts);
      });
      
      commentsData.data?.forEach(c => {
        const counts = activityCounts.get(c.user_id) || { posts: 0, comments: 0, likes: 0 };
        counts.comments++;
        activityCounts.set(c.user_id, counts);
      });
      
      likesData.data?.forEach(l => {
        const counts = activityCounts.get(l.user_id) || { posts: 0, comments: 0, likes: 0 };
        counts.likes++;
        activityCounts.set(l.user_id, counts);
      });

      // Calculate scores and filter by distance
      const scored: ActivityScore[] = profiles
        .map(profile => {
          const activity = activityCounts.get(profile.user_id) || { posts: 0, comments: 0, likes: 0 };
          const score = activity.posts + 0.5 * activity.comments + 0.25 * activity.likes;
          
          // Calculate distance if coordinates available
          let distanceKm: number | undefined;
          if (userLat && userLng && profile.latitude && profile.longitude) {
            distanceKm = calculateDistance(userLat, userLng, profile.latitude, profile.longitude);
          }

          return {
            id: profile.user_id,
            name: profile.username || "Farmer",
            avatarUrl: profile.avatar_url || undefined,
            city: profile.location || undefined,
            distanceKm,
            crops: [], // TODO: fetch from communities or profile_tags
            posts7d: activity.posts,
            score,
          };
        })
        .filter(p => {
          // Filter by distance if available
          if (p.distanceKm !== undefined) {
            return p.distanceKm <= radiusKm && p.score > 0;
          }
          // Otherwise include if they have activity
          return p.score > 0;
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);

      return scored;
    },
    enabled: true,
  });
}

// Haversine formula for distance calculation
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}
